// @flow
import * as React from 'react'
import glamorous from 'glamorous'
import Helmet from 'react-helmet'
import { throttle } from 'lodash'
import {
  Wrapper,
  Grid,
  Slideshow,
  SlideshowTrigger,
  GalleryImage,
  Markdown,
} from 'components'
import { typography, margin, colors, transition } from 'style'
import ArrowIcon from 'assets/icons/Arrow.svg'
import { trackSlideshowOpen } from 'analytics'

import type { Location, RouterHistory } from 'react-router-dom'
import type { ContentfulAsset, PhotoswipeOptions } from 'types'

type ContentfulGallery = {
  title: string,
  description: ?{
    childMarkdownRemark: {
      html: string,
    },
  },
  images: Array<ContentfulAsset>,
}

const GalleryTitle = glamorous.h1({
  ...typography.title1,
  ...margin.y(4),
  textAlign: 'center',
})

const BackButton = glamorous
  .button({
    ...margin.top(4),
    ...margin.left(2),
    ...typography.nav,
    ...transition.medium('color'),
    color: colors.primary,
    ':hover': {
      color: colors.primaryDark,
      '> i': {
        transform: 'translateX(-3px)',
      },
    },
    ':focus': {
      color: colors.primaryDark,
      '> i': {
        transform: 'translateX(-3px)',
      },
    },
    '> i': {
      ...transition.medium('transform'),
      ...margin.right(2),
      display: 'inline-block',
    },
  })
  .withProps({ onMouseDown: e => e.preventDefault() })

const capitalize = string => `${string[0].toUpperCase()}${string.slice(1)}`

const countLines = (element: HTMLElement) => {
  const divHeight = element.offsetHeight
  const lineHeight = parseInt(getComputedStyle(element).lineHeight)
  return divHeight / lineHeight
}

const mapFilterToString = filter =>
  filter === '*' ? 'All' : capitalize(filter)

type Props = {
  history: RouterHistory,
  location: Location,
  data: {
    contentfulGallery: ContentfulGallery,
    site: {
      siteMetadata: {
        siteUrl: string,
      },
    },
  },
}

type State = {
  isOpen: boolean,
  options: PhotoswipeOptions,
  centerDescription: boolean,
}

class GalleryTemplate extends React.Component<Props, State> {
  state = {
    isOpen: false,
    options: { index: -1 },
    centerDescription: false,
  }

  componentDidMount() {
    window.addEventListener('resize', this.onResize)
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize)
  }

  onResize = throttle(() => {
    this.maybeCenterDescription()
  }, 300)

  setDescription = (ref: ?HTMLElement) => {
    this.description = ref
    this.maybeCenterDescription()
  }

  maybeCenterDescription = () => {
    if (this.description) {
      const descriptionElement = ((this.description
        .firstElementChild: any): HTMLElement) // flowlint-line unclear-type:off

      if (countLines(descriptionElement) === 1) {
        this.setState({ centerDescription: true })
      } else {
        this.setState({ centerDescription: false })
      }
    }
  }

  openSlideshow = (itemIndex: number) => (e: MouseEvent) => {
    e.preventDefault()
    const getThumbBoundsFn = index => {
      const thumbnail = this.thumbnails[index]
      if (thumbnail == null) return null
      const img =
        thumbnail.getElementsByTagName('img')[0] ||
        thumbnail.getElementsByTagName('video')[0]
      const pageYScroll =
        window.pageYOffset ||
        (document.documentElement && document.documentElement.scrollTop)
      const rect = img.getBoundingClientRect()
      return { x: rect.left, y: rect.top + pageYScroll, w: rect.width }
    }

    this.setState({
      isOpen: true,
      options: {
        index: itemIndex,
        getThumbBoundsFn,
      },
    })

    const { location } = this.props
    trackSlideshowOpen(location)
  }

  handleClose = () => {
    this.setState({
      isOpen: false,
    })
  }

  thumbnails: Array<?HTMLElement>

  description: ?HTMLElement

  render() {
    const { isOpen, options, centerDescription } = this.state
    const { data, location, history } = this.props
    const { state } = location

    const gallery = data.contentfulGallery
    const description =
      gallery.description != null
        ? gallery.description.childMarkdownRemark
        : undefined

    const { siteUrl } = data.site.siteMetadata
    return (
      <Wrapper>
        <Helmet>
          <title>{gallery.title}</title>
          <meta
            name="description"
            content={`Photos of ${gallery.title} by Melissa O’Hearn`}
          />
          <link
            rel="canonical"
            href={`${siteUrl}${location.pathname.replace(/\/$/, '')}`}
          />
        </Helmet>
        {state &&
          state.filter && (
            <BackButton onClick={history.goBack}>
              <i>
                <ArrowIcon />
              </i>
              {`Back to ${mapFilterToString(state.filter)}`}
            </BackButton>
          )}
        <GalleryTitle>{gallery.title}</GalleryTitle>
        {description && (
          <Markdown
            alignCenter={centerDescription}
            innerRef={this.setDescription}
            dangerouslySetInnerHTML={{
              __html: description.html,
            }}
          />
        )}
        <Grid>
          {gallery.images.map(({ id, ...image }, index) => (
            <Grid.Item key={id}>
              <SlideshowTrigger
                onClick={this.openSlideshow(index)}
                innerRef={node => {
                  this.thumbnails = this.thumbnails || []
                  this.thumbnails[index] = node
                }}
              >
                <GalleryImage image={{ id, ...image }} />
              </SlideshowTrigger>
            </Grid.Item>
          ))}
        </Grid>
        {isOpen && (
          <Slideshow
            images={gallery.images}
            options={options}
            location={location}
          />
        )}
      </Wrapper>
    )
  }
}

export default GalleryTemplate

export const pageQuery = graphql`
  query GalleryTemplateQuery($id: String!) {
    contentfulGallery(id: { eq: $id }) {
      title
      description {
        childMarkdownRemark {
          html
        }
      }
      images {
        id
        title
        sizes(quality: 80) {
          ...GatsbyContentfulSizes_withWebp
        }
        resolutions(width: 2000, quality: 100) {
          ...GatsbyContentfulResolutions_withWebp
        }
        file {
          url
          contentType
        }
        fields {
          aspectRatio
        }
      }
    }
    ...SiteUrl
  }
`



// WEBPACK FOOTER //
// ./src/templates/gallery.js